<template>
  <v-container
    fluid
    class="br-page"
    :class="{ 'br-page--dark': $vuetify.theme.dark }"
  >
    <section>
      <!-- Top Navigation / Tab Switcher -->
      <v-card class="br-stock-nav mb-3" flat>
        <v-card-text class="d-flex align-center flex-wrap py-2 px-3">
          <div class="d-flex align-center mr-4">
            <span class="br-accent-bar mr-2" />
            <h1 class="br-page-title mb-0">{{ pageTitle }}</h1>
          </div>

          <v-spacer />

          <div class="br-tabs">
            <div
              class="br-tab"
              :class="{ 'br-tab--active': activeTab === 'inventory' }"
              @click="switchTab('inventory')"
            >
              <v-icon
                size="13"
                class="mr-1"
                :color="activeTab === 'inventory' ? 'primary' : 'grey'"
              >
                fal fa-boxes
              </v-icon>
              {{ $t("stock_report") || "ລາຍງານສະຕັອກ" }}
            </div>
            <div
              class="br-tab"
              :class="{ 'br-tab--active': activeTab === 'alert' }"
              @click="switchTab('alert')"
            >
              <v-icon
                size="13"
                class="mr-1"
                :color="activeTab === 'alert' ? 'warning' : 'grey'"
              >
                fal fa-exclamation-triangle
              </v-icon>
              {{ $t("stock_alert_report") || "ແຈ້ງເຕືອນສະຕັອກຕ່ຳ" }}
            </div>
            <div
              class="br-tab"
              :class="{ 'br-tab--active': activeTab === 'expired' }"
              @click="switchTab('expired')"
            >
              <v-icon
                size="13"
                class="mr-1"
                :color="activeTab === 'expired' ? 'error' : 'grey'"
              >
                fal fa-calendar-times
              </v-icon>
              {{ $t("expired_products") || "ສິນຄ້າໝົດອາຍຸ" }}
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Keep-alive tab content -->
      <keep-alive>
        <tab-stock-inventory v-if="activeTab === 'inventory'" key="inventory" />
        <tab-stock-alert v-else-if="activeTab === 'alert'" key="alert" />
        <tab-stock-expired v-else key="expired" />
      </keep-alive>
    </section>
  </v-container>
</template>

<script>
import TabStockInventory from "@/pages/reports/stock/TabStockInventory.vue";
import TabStockAlert from "@/pages/reports/stock/TabStockAlert.vue";
import TabStockExpired from "@/pages/reports/stock/TabStockExpired.vue";

export default {
  name: "StockReports",
  components: {
    TabStockInventory,
    TabStockAlert,
    TabStockExpired,
  },
  data() {
    return {
      activeTab: "inventory",
    };
  },
  computed: {
    pageTitle() {
      if (this.activeTab === "alert") {
        return this.$t("stock_alert_report") || "ແຈ້ງເຕືອນສະຕັອກຕ່ຳ";
      }
      if (this.activeTab === "expired") {
        return this.$t("expired_products") || "ສິນຄ້າໝົດອາຍຸ";
      }
      return this.$t("stock_report") || "ລາຍງານສະຕັອກ";
    },
  },
  watch: {
    "$route.query.tab": {
      immediate: true,
      handler(tab) {
        if (tab && ["inventory", "alert", "expired"].includes(tab)) {
          this.activeTab = tab;
        }
      },
    },
  },
  methods: {
    switchTab(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      if (this.$route.query.tab !== tab) {
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              tab: tab === "inventory" ? undefined : tab,
            },
          })
          .catch(() => {});
      }
    },
  },
};
</script>

<style scoped>
.br-page {
  --br-accent: var(--v-primary-base, #5b39c9);
  --br-border: #e2e8f0;
  --br-muted: #94a3b8;
  --br-text: #141021;
  --br-radius-lg: 16px;
  --br-radius-md: 10px;
  max-width: 100%;
  background: #f6f6fa;
}

.br-page--dark {
  --br-text: #ffffff;
  background: #1b1d30;
}

.br-page section {
  width: 100%;
}

.br-stock-nav {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
}

.br-page--dark .br-stock-nav {
  background: #232742 !important;
  border-color: #313759 !important;
}

.br-accent-bar {
  display: inline-block;
  width: 4px;
  height: 20px;
  border-radius: 2px;
  background: var(--br-accent);
}

.br-page-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--br-text);
  letter-spacing: -0.01em;
}

/* Tabs */
.br-tabs {
  display: inline-flex;
  height: 38px;
  box-sizing: border-box;
  align-items: center;
  gap: 4px;
  background: #f1f2f7;
  border: 1px solid #e2e8f0;
  padding: 3px;
  border-radius: 10px;
}

.br-page--dark .br-tabs {
  background: #1b1d30;
  border-color: rgba(154, 140, 240, 0.16);
}

.br-tab {
  height: 30px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  padding: 4px 12px;
  border-radius: 7px;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.br-tab:hover {
  color: #141021;
}

.br-page--dark .br-tab:hover {
  color: #e2e8f0;
}

.br-tab--active {
  background: #ffffff !important;
  color: #5b39c9 !important;
  font-weight: 700;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.br-page--dark .br-tab--active {
  background: rgba(154, 140, 240, 0.18) !important;
  color: #c4b5fd !important;
  box-shadow: none;
}
</style>
